/**
 * The rep views, shared by the widget and the standalone application.
 *
 * They live here rather than in either app because the zone rule forbids `frontend/*` importing each other,
 * and that rule is right: the two surfaces have different authentication and different threat models, and a
 * shared import would be the seam through which one's code reached the other's bundle. What they legitimately
 * share is what an exchange looks like.
 *
 * So every panel takes data and callbacks and owns no client. The widget's client sends
 * `Authorization: Widget <token>` and the standalone one sends a provider bearer; neither difference is
 * visible from here, which is the property that keeps this file honest.
 *
 * Nothing here names a colour, a radius or a font. Everything reads through the tokens in `tokens.ts`, so a
 * customer retheme under D-01 is one file and not a specificity fight.
 */
import type { ReactNode } from 'react';
import type { Contact, DocumentRequestItem, DocumentSummary, Exchange, ResolvedActivityEvent } from '@iqz-ddds/contracts';
import { FormattedRequest } from '../FormattedRequest';
import { Avatar, Button, Card, Notice, Progress, Row, StatusPill } from '../primitives';

/** A date a rep reads, not an instant a machine parses. */
function shortDate(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? ''
    : at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export interface ExchangeHeaderProps {
  readonly exchange: Exchange;
  readonly contact: Contact | undefined;
  /** Ask for a document. Absent where the rep may not change the checklist. */
  readonly onRequest?: (() => void) | undefined;
  /** Send one. Absent on a surface that cannot upload. */
  readonly onSend?: (() => void) | undefined;
  /** Disables both while a write from this view is in flight. */
  readonly busy?: boolean;
}

/**
 * Who this is about, and the two things a representative can do about it.
 *
 * The contact may be undefined: the exchange projection carries `contactId` and no name, so a surface has to
 * fetch the person separately and may not have yet. Showing the exchange title alone is better than blocking
 * the whole panel on a second request.
 *
 * ## Why the actions are here
 *
 * DG.18a. This view used to offer asking, inviting and uploading as three peers scattered across three
 * panels, which is three decisions where a representative has one intention. Both verbs now sit above
 * the panels and everything below is a consequence of one of them - the checklist is what "request"
 * produced, the document lists are what "send" produced.
 *
 * They are buttons rather than links because neither navigates: `onRequest` moves focus to the field
 * that is already on the screen, and `onSend` opens the file picker. A representative who wants the
 * detail scrolls; a representative who knows what they came for does not have to.
 */
export function ExchangeHeader({
  exchange,
  contact,
  onRequest,
  onSend,
  busy = false,
}: ExchangeHeaderProps): React.JSX.Element {
  return (
    <header className="ddds-rep-header">
      <span className="ddds-rep-header__who">
        {contact !== undefined && <Avatar name={contact.displayName} size="large" />}
        <span>
          <h1 className="ddds-title">{contact?.displayName ?? exchange.title}</h1>
          <span className="ddds-rep-header__meta">
            {contact?.email}
            {exchange.state === 'archived' && (
              <>
                {contact !== undefined && ' · '}
                <StatusPill tone="quiet">Archived</StatusPill>
              </>
            )}
          </span>
        </span>
      </span>

      {(onRequest !== undefined || onSend !== undefined) && (
        <span className="ddds-rep-header__actions">
          {onRequest !== undefined && (
            <Button variant="quiet" width="auto" disabled={busy} onClick={onRequest}>
              Request a document
            </Button>
          )}
          {onSend !== undefined && (
            <Button width="auto" disabled={busy} onClick={onSend}>
              Send a document
            </Button>
          )}
        </span>
      )}
    </header>
  );
}

export interface ChecklistPanelProps {
  readonly requests: readonly DocumentRequestItem[];
  /** Undefined while the rep may not change the checklist, which hides the withdrawal control. */
  readonly onWithdraw?: (requestId: string) => void;
  readonly pending?: boolean;
  /** Nudge the contact about what is already outstanding. See the footer, below. */
  readonly onRemind: () => void;
  /** Only the reminder's own state. A checklist change is `pending` and says nothing about this (DG.22). */
  readonly reminding: boolean;
  /** False when nothing is awaited and nothing has been shared, which is the 409 the endpoint would answer. */
  readonly canRemind: boolean;
  /**
   * True once the endpoint has accepted a reminder for this exchange, which replaces the button with what
   * became of it. Its own flag rather than the absence of `reminding`, because "not in flight" is also
   * every moment before the rep pressed anything. See the footer.
   */
  readonly reminded: boolean;
}

/**
 * What the rep is still waiting for.
 *
 * Withdrawn items are hidden rather than struck through, matching the prospect surface: a rep who withdrew a
 * request has decided it is not wanted, and a list that keeps showing it invites asking again. The audit trail
 * is where a withdrawal remains visible, which is where it belongs.
 *
 * ## And a fulfilled one leaves too (DG.41)
 *
 * A request that has been answered is a document, and the document is in `ReceivedPanel` under its real
 * filename. Keeping the row here as well showed a rep the same fact twice - once as "Passport / Received"
 * and again as "passport-front.jpg" - and the screenshot that prompted DG.41 was four items in exactly
 * that state, which is a checklist that has stopped being a list of things to do.
 *
 * So this panel is now the outstanding ones only, and the empty sentence changed with it: "Nothing
 * requested yet" is false for a rep who asked for three things and received all three, which is the
 * commonest way this list empties.
 *
 * ## It no longer starts anything
 *
 * DG.33 took the add field out of here. This card answers "what have I asked for" and nothing else; the
 * asking happens in `RequestDocumentsDialog`, reached from the header or from a host's own button. The
 * panel as a whole is a record of what has happened, and a form in the middle of a record is a second
 * thing to read past on the way to the answer.
 *
 * ## Except the reminder, and why it is a footnote rather than a verb
 *
 * A prospect is onboarded by being asked for something or sent something: either message reaches them, and
 * for somebody who has never signed in it carries the one-click link. So there is no introduction left for
 * a representative to send by hand, and the card that used to offer one is gone.
 *
 * What is left is the person who was told and has not acted, which is a remark about this list rather than
 * a third thing to do with it. DG.18a's whole point is that asking and sending are the two actions, and a
 * reminder promoted to a peer of them - a card of its own, or a third header button - would undo that for
 * the rarer of the two cases. Hence the panel's footer, the quietest button variant there is, and no
 * heading of its own.
 *
 * Disabled rather than hidden when there is nothing to remind about: a control that comes and goes is one
 * a rep cannot learn, and the reason is short enough to say out loud.
 *
 * ## And why a pressed reminder says something rather than nothing
 *
 * The two automatic notifications schedule a wake for the moment their coalesced message becomes due. The
 * reminder deliberately does not, and that is settled: its outbox row is due immediately and its one-minute
 * idempotency key is a *deduplication* window rather than a delay, so waking the dispatcher early would
 * deliver the first press inside that window and turn a second press twenty seconds later into a second
 * email - reversing "two presses, one message", which is the property the window exists for. So the message
 * waits for the once-a-minute `outbox.dispatch` cron, and up to about a minute of silence is the normal case.
 *
 * A label that flipped to "Reminding" and then back to "Remind them" spent that minute saying the press did
 * not take, so the rep presses again, the window absorbs it exactly as designed, and they get neither a
 * second message nor any feedback. Hence a completed state: the control stops offering itself and says what
 * became of the press, in the words the endpoint can actually stand behind. It answers 202 and carries no
 * `delivered` flag, because the message is written to the outbox in the audit transaction and dispatched
 * afterwards - so "queued" is true and "sent" would be a claim we have not checked.
 */
export function ChecklistPanel({
  requests,
  onWithdraw,
  pending = false,
  onRemind,
  reminding,
  canRemind,
  reminded,
}: ChecklistPanelProps): React.JSX.Element {
  // Outstanding only. A fulfilled request is a document and lives in `ReceivedPanel`; see above.
  const live = requests.filter((request) => request.state === 'awaiting');

  return (
    <Card title="Requested from them">
      {live.length === 0 ? (
        <p className="ddds-empty">Nothing outstanding.</p>
      ) : (
        <ul className="ddds-list">
              {live.map((request) => (
                <Row
                  key={request.id}
                  name={<FormattedRequest html={request.label} />}
                  meta={`Asked ${shortDate(request.requestedAt)}`}
                  action={<ChecklistAction request={request} onWithdraw={onWithdraw} pending={pending} />}
                />
              ))}
            </ul>
      )}

      {/*
        `.ddds-rep-add` rather than a class of its own: it is already the footer strip under a card's
        body - a rule above, a gap, and whatever the panel offers next - and the reminder wants exactly
        that and nothing more. One less class to retheme under D-01.
      */}
      <div className="ddds-rep-add">
        {reminded ? (
          /*
            One sentence for what happened and one for the wait, and no more: this is a panel footer and a
            banner here would make the rarer of two cases the loudest thing on the screen.

            An <output> rather than a paragraph, because it replaces the control the rep just pressed:
            role=status comes with the element, so somebody who cannot see a button become a sentence is
            told. `.ddds-empty` is the class for ordinary quiet copy standing in for something, and it
            already carries the `display: block` an <output> needs.

            "In the next minute or so" rather than a figure. The cron's phase drifts, so any number would be
            wrong for most presses, and a rep who was promised eleven seconds is worse off than one who was
            told to expect a wait. What the sentence is really for is the third clause: there is nothing
            further to do, so nobody goes looking for a second press or reloads the panel hunting for one.
          */
          <output className="ddds-empty">
            A reminder is queued for them. It goes out in the next minute or so, and there is nothing else to do here.
          </output>
        ) : (
          <>
            <Button variant="quiet" size="small" disabled={reminding || !canRemind} onClick={onRemind}>
              {reminding ? 'Reminding' : 'Remind them'}
            </Button>
            {/*
              The small quiet line, which is `.ddds-row__meta` worn outside a row - as `QueueRow` above
              already does, and for the same reason: it is the one class that means "a smaller, quieter
              second line", and a hint set in body copy beside a disabled button reads louder than the
              control it is explaining. The paragraph's own margin is what separates the two.

              Inside this branch rather than beside it, so a reminder that was queued and then left nothing
              outstanding - remind, then withdraw the last item - shows the outcome alone. Both at once
              would have the footer contradict itself.
            */}
            {!canRemind && <p className="ddds-row__meta">Nothing outstanding to remind about.</p>}
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * The right-hand side of one checklist row: the state, or a way to withdraw.
 *
 * Its own component rather than a chain of conditionals inside the row, because the two cases are two
 * different things - a state and a control - and reading them as early returns says that better than a
 * ternary does.
 *
 * ## There is no "Received" arm any more, and deleting it was the point
 *
 * DG.41 filtered this panel down to `state === 'awaiting'`, so a fulfilled request never reaches here.
 * The arm that rendered a done pill is deleted rather than left unreachable, and that is deliberate in
 * both directions. Left in place it would be a dead branch that silently resurrects the duplicate row the
 * moment somebody widens the filter again. And removed *without* deleting it - the other order - a
 * fulfilled item would fall through to the `onWithdraw === undefined` arm below and be labelled
 * "Awaiting", which is a lie about somebody's affairs on a screen a rep acts from.
 */
function ChecklistAction({
  request,
  onWithdraw,
  pending,
}: {
  readonly request: DocumentRequestItem;
  readonly onWithdraw: ((requestId: string) => void) | undefined;
  readonly pending: boolean;
}): React.JSX.Element {
  // No `onWithdraw` means the rep may not change the checklist, so there is nothing to offer but the state.
  if (onWithdraw === undefined) return <StatusPill tone="waiting">Awaiting</StatusPill>;

  return (
    <Button
      variant="quiet"
      size="small"
      disabled={pending}
      onClick={() => onWithdraw(request.id)}
      label={`Withdraw ${request.label}`}
    >
      Withdraw
    </Button>
  );
}

/**
 * One file in a batch a rep handed over, in the four states it passes through.
 *
 * `sending` has a progress bar because there is progress to show. `checking` does not: R5's scan
 * reports nothing until it has a verdict, so a bar sitting at 100% would be claiming to know
 * something. The same split the prospect surface makes, for the same reason (DG.14).
 *
 * `waiting` exists because DG.33 let a rep pick several at once and they are uploaded one at a time -
 * a file with no row at all would look dropped rather than queued. `done` carries whatever there is to
 * say about the result, and usually there is nothing: a committed document is in the `Sent` list, which
 * is the confirmation, and a row repeating it would be the same fact twice.
 */
export interface QueuedSend {
  /** Stable for the life of the batch. Two files can share a name, so this is not one. */
  readonly key: string;
  readonly filename: string;
  readonly sentBytes: number;
  readonly totalBytes: number;
  readonly stage: 'waiting' | 'sending' | 'checking' | 'done';
  readonly outcome: string | null;
}

/**
 * What either document panel needs, which is the same three things.
 *
 * `documents` is the full list in both cases and each panel filters it itself. The alternative - the
 * caller filtering, and passing each panel the half it should show - moves a decision about what "they
 * sent" means out of the component that names it, and puts it in two callers who have to agree.
 */
export interface DocumentListPanelProps {
  readonly documents: readonly DocumentSummary[];
  readonly onDownload: (document: DocumentSummary) => void;
  readonly busyId?: string | undefined;
}

/**
 * What arrived, which is one of the two directions.
 *
 * Figure 4 draws both as one table with a direction column; splitting them is the same decision the prospect
 * surface made, for the same reason: "what they sent me" and "what I sent them" are answers to different
 * questions and a rep scanning for one does not want to filter the other by eye.
 *
 * Two named exports rather than one component taking a direction, because the empty sentences are not
 * symmetrical and a `direction` prop would invite one sentence for both. "Nothing has arrived yet" is a state
 * to wait on; "You have not shared anything" is a prompt to act. A shared prop would lose that.
 *
 * The `<Card title>` stays in both, now that DG.41 shows one panel at a time. A real `<h2>` is how somebody
 * on a screen reader navigates *within* the view, and "four cards competing for one screen" is answered by
 * showing one of them, not by taking the headings off all four.
 *
 * Soft-deleted documents are shown with their removal date rather than hidden. R4 keeps the row and a rep who
 * removed something needs to see that it happened - the prospect's own view is what hides it.
 */
export function ReceivedPanel({ documents, onDownload, busyId }: DocumentListPanelProps): React.JSX.Element {
  return (
    <Card title="They sent">
      <DocumentList
        documents={documents.filter((document) => document.origin === 'contact')}
        onDownload={onDownload}
        busyId={busyId}
        empty="Nothing has arrived yet."
      />
    </Card>
  );
}

/** What the rep handed over. `ReceivedPanel`, above, carries the reasoning for both. */
export function SentPanel({ documents, onDownload, busyId }: DocumentListPanelProps): React.JSX.Element {
  return (
    <Card title="You sent">
      <DocumentList
        documents={documents.filter((document) => document.origin === 'rep')}
        onDownload={onDownload}
        busyId={busyId}
        empty="You have not shared anything."
      />
    </Card>
  );
}

export interface SendQueueProps {
  /** The batch in flight, in the order it was picked. Renders nothing at all when empty. */
  readonly queue: readonly QueuedSend[];
}

/**
 * The batch a rep just handed over, and no longer inside the panel it will land in.
 *
 * The control that starts an upload is not here and is not on any panel - it is `SendDocumentsDialog`,
 * opened from the header or from a host's own button (DG.33). What stays is the *reporting*, and the
 * dialog closes as the transfer starts, so this is the only place a rep sees what became of four files
 * they picked a moment ago.
 *
 * ## Why it is out of "You sent" (DG.41)
 *
 * The exchange view now renders this *below* the tab panel, and only the selected panel is mounted. A
 * queue nested in "You sent" would therefore unmount the instant a rep switched to Activity, taking the
 * progress of a transfer they started with it - DG.22's defect in a new place, and the worse version of
 * it, because here the bytes really are still moving.
 *
 * Null on an empty queue rather than an empty element, so the region is absent from the document instead
 * of being a stray border under the panel.
 */
export function SendQueue({ queue }: SendQueueProps): React.JSX.Element | null {
  if (queue.length === 0) return null;

  return (
    <div className="ddds-rep-sending">
      {queue.map((entry) => (
        <QueueRow key={entry.key} entry={entry} />
      ))}
    </div>
  );
}

/** One file's wait, in whichever of the four states it is in. See `QueuedSend`. */
function QueueRow({ entry }: { readonly entry: QueuedSend }): React.JSX.Element | null {
  if (entry.stage === 'done') {
    // Silence when there is nothing to say: the document is in the `Sent` list, which is the confirmation.
    return entry.outcome === null ? null : <Notice tone="warn">{entry.outcome}</Notice>;
  }

  if (entry.stage === 'waiting') {
    return (
      <>
        <p className="ddds-row__name">{entry.filename}</p>
        <output className="ddds-row__meta">Waiting to send.</output>
      </>
    );
  }

  if (entry.stage === 'checking') {
    return (
      <>
        <p className="ddds-row__name">{entry.filename}</p>
        {/* An <output> carries role=status implicitly, so the change is announced without the attribute
            having to be right. No bar: the scanner reports nothing until it has a verdict. */}
        <output className="ddds-row__meta">Sent. Checking this file before it is added.</output>
      </>
    );
  }

  return (
    <>
      <p className="ddds-row__name">{entry.filename}</p>
      <Progress
        value={entry.totalBytes === 0 ? 0 : entry.sentBytes / entry.totalBytes}
        label={`Sending ${entry.filename}`}
      />
    </>
  );
}

function DocumentList({
  documents,
  onDownload,
  busyId,
  empty,
}: {
  readonly documents: readonly DocumentSummary[];
  readonly onDownload: (document: DocumentSummary) => void;
  readonly busyId: string | undefined;
  readonly empty: string;
}): React.JSX.Element {
  if (documents.length === 0) return <p className="ddds-empty">{empty}</p>;

  return (
    <ul className="ddds-list">
      {documents.map((document) => (
        <Row
          key={document.id}
          name={document.name}
          meta={
            document.removedAt === null
              ? `Added ${shortDate(document.addedAt)}`
              : `Removed ${shortDate(document.removedAt)}`
          }
          action={<DocumentAction document={document} onDownload={onDownload} busyId={busyId} />}
        />
      ))}
    </ul>
  );
}

/**
 * The right-hand side of one document row.
 *
 * Three states in the order a rep meets them, as early returns rather than a nested ternary: removed, not
 * yet arrived, and openable.
 */
function DocumentAction({
  document,
  onDownload,
  busyId,
}: {
  readonly document: DocumentSummary;
  readonly onDownload: (document: DocumentSummary) => void;
  readonly busyId: string | undefined;
}): React.JSX.Element {
  if (document.removedAt !== null) return <StatusPill tone="quiet">Removed</StatusPill>;
  if (document.currentVersionId === null) {
    // Committed rows exist before their bytes do: the pipeline scans and commits asynchronously, so
    // "checking" is a real state a rep will see and not an error.
    return <StatusPill tone="waiting">Checking</StatusPill>;
  }

  const busy = busyId === document.id;
  return (
    <Button
      variant="quiet"
      size="small"
      disabled={busy}
      onClick={() => onDownload(document)}
      label={`Download ${document.name}`}
    >
      {busy ? 'Opening' : 'Open'}
    </Button>
  );
}

/**
 * Everything a trail line has to work with.
 *
 * The two resolved names come from the event; `custodianName` is on the envelope rather than on each
 * event, because it is one fact about the exchange (see `activitySchema`). Only `guest.grant_issued`
 * reads it, and it is handed to every entry anyway: a table where one of twenty-seven functions has a
 * different signature is a table somebody has to read twice before they can add to it.
 */
export interface ActionNames {
  readonly actorName: string | null;
  readonly subjectName: string | null;
  readonly custodianName: string | null;
}

/**
 * Active where we know who, passive where we do not.
 *
 * The passive is the honest degradation, and "Somebody opened the link" is not: a null actor name does
 * not mean one unidentified person, it means the id behind it resolves to nothing nameable - a service
 * principal, or a contact whose row is gone. Inventing a person would put a claim in the trail that D-14
 * makes the primary compliance control.
 */
function by(actorName: string | null, active: string, passive: string): string {
  return actorName === null ? passive : `${actorName} ${active}`;
}

/**
 * A thing the sentence names, quoted, or a plain noun where there is no name.
 *
 * Quoted because these are somebody else's words inside ours. A checklist label is whatever the
 * representative typed, up to two hundred characters of it, so "Michael asked for Please send last
 * year's T4 and a void cheque" is unreadable where the quoted form is not. It also sidesteps the
 * capital: a label or a filename starts with one and a sentence cannot lowercase it blindly, because
 * "IRS form 1040" would become "iRS form 1040".
 *
 * Null is the ordinary answer rather than the exceptional one. Five subject kinds reach an exchange
 * chain with nothing nameable behind them at all - `staged_upload`, `guest_grant`, `guest_challenge`,
 * `guest_session` and `guest_device` are bytes and credentials - so every sentence below that names a
 * subject has to read as English without one.
 */
function named(subjectName: string | null, fallback: string): string {
  return subjectName === null ? fallback : `"${subjectName}"`;
}

/**
 * Audit actions, as sentences (DG.23).
 *
 * This was twenty-five slugs - "Link opened", "Code emailed", "Upload started" - which are event names
 * rather than a record of what happened to whom. With both names resolved on every event the same trail
 * reads as "Michael Reyes asked for a document" and "Dana Whitfield opened the secure link", and that is
 * what makes it usable in the room: D-14 makes this trail the primary compliance control, so the person
 * reading it under pressure is often not the person who built the system.
 *
 * Anything unmapped falls through to the raw action rather than to "unknown". There are forty-seven
 * actions in the catalogue and this maps twenty-seven of them, and a trail that hid an event it did not
 * recognise would be worse than one showing a machine name. `backend/test/action-words.spec.ts` is what
 * keeps the two lists in step: every catalogue action either has a sentence here or a reason there.
 *
 * ## Why this is exported
 *
 * DG.18a says a representative never reads the word "invitation": it names the credential we mint rather
 * than the thing they are doing, and stakeholders report that reps do not recognise it. That rule was
 * guarded by a test scoped to the card that offered to email a link - the one place the word had already
 * been removed from - so it passed while `guest.grant_issued` rendered "Invitation sent" in the trail
 * beside it, for months. The guard reads this table, which is where the word can actually survive, and it
 * now sweeps each entry with names and without them, because a template has two branches to get wrong.
 */
export const ACTION_WORDS: Readonly<Record<string, (names: ActionNames) => string>> = {
  'exchange.created': ({ actorName }) => by(actorName, 'opened this exchange', 'This exchange was opened'),
  // No new title in the sentence: the resolved subject of a retitle is the exchange's title *now*, which
  // is not the one this event set. Saying which words were chosen would be a guess dressed as a record.
  'exchange.retitled': ({ actorName }) => by(actorName, 'renamed this exchange', 'This exchange was renamed'),
  // One sentence for both kinds of reminder, because the surface cannot tell them apart. The event's
  // payload says whether it chased a requested document or a shared one, but no projection publishes a
  // payload: `activityBody` in backend/src/api/v1/wire.ts lists seven fields and that is not one of them.
  // A sentence that named the kind would be inventing a distinction the wire deliberately withholds.
  'exchange.reminded': ({ actorName }) => by(actorName, 'sent a reminder', 'A reminder was sent'),
  'exchange.archived': ({ actorName }) => by(actorName, 'archived this exchange', 'This exchange was archived'),

  'document.requested': ({ actorName, subjectName }) =>
    by(actorName, `asked for ${named(subjectName, 'a document')}`, `${named(subjectName, 'A document')} was requested`),
  'document.request_withdrawn': ({ actorName, subjectName }) =>
    by(
      actorName,
      `withdrew the request for ${named(subjectName, 'a document')}`,
      `The request for ${named(subjectName, 'a document')} was withdrawn`,
    ),
  // The subject is a staged upload, which has no name to resolve, so neither form names one.
  'document.uploaded': ({ actorName }) => by(actorName, 'started sending a file', 'An upload started'),
  'document.downloaded': ({ actorName, subjectName }) =>
    by(actorName, `opened ${named(subjectName, 'a document')}`, `${named(subjectName, 'A document')} was opened`),
  'document.deleted': ({ actorName, subjectName }) =>
    by(actorName, `removed ${named(subjectName, 'a document')}`, `${named(subjectName, 'A document')} was removed`),
  'document.associated': ({ actorName, subjectName }) =>
    by(
      actorName,
      `added ${named(subjectName, 'a document')} from the upload link`,
      `${named(subjectName, 'A document')} was added from the upload link`,
    ),

  /*
    The five with a service actor, and they stay impersonal.

    A commit, a scan verdict and an expiry are the pipeline finishing something nobody is watching, so
    there is no actor to name and none of these takes one. Naming the rep here would be the mistake
    `guest.grant_issued` below is careful about, without even that event's excuse: a scan verdict is not a
    consequence of an instruction anybody gave, so there is no decision to point at.
  */
  'document.committed': ({ subjectName }) => `${named(subjectName, 'A document')} was added to this exchange`,
  'document.version_added': ({ subjectName }) => `A new version of ${named(subjectName, 'a document')} was added`,
  'document.scan_clean': ({ subjectName }) => `${named(subjectName, 'The file')} was scanned and found clean`,
  'document.scan_infected': ({ subjectName }) =>
    `The scan found something harmful in ${named(subjectName, 'the file')}, so it was not added`,
  'document.scan_indeterminate': ({ subjectName }) =>
    `The scan could not reach a verdict on ${named(subjectName, 'the file')}, so it is being held`,
  'document.upload_expired': () => 'An upload was never finished, and its bytes have expired',

  /*
    The first event on an exchange chain, and the one with nobody behind it.

    Since DG.35 the grant is minted in the outbox dispatcher while the message is rendered, so its actor
    is the service principal and `actorName` is null. Both obvious readings mislead a D-14 reviewer.
    Naming the representative as the actor files a decision against somebody who did not make it in that
    transaction. Saying only that a system did it leaves a personless first event with nothing pointing at
    the decision behind it, on the chain a reviewer reads to find out why this person was contacted at all.

    So the sentence does both jobs: it says plainly that nobody pressed anything, and it names whose
    instruction the link went out with. The custodian comes off the envelope, which is the only place that
    name exists - it is a fact about the exchange and not about this event.

    The possessive is added whatever the name ends in, so a custodian called Reyes gets "Reyes's request".
    That is a style choice; guessing at a plural from a final s is how you get "Michael Jame's".
  */
  'guest.grant_issued': ({ custodianName }) =>
    custodianName === null
      ? "Secure link sent automatically, with the representative's request"
      : `Secure link sent automatically, with ${custodianName}'s request`,

  'guest.grant_opened': ({ actorName }) => by(actorName, 'opened the secure link', 'The secure link was opened'),
  'guest.grant_consumed': ({ actorName }) =>
    by(actorName, 'used the secure link to sign in', 'The secure link was used to sign in'),
  // Impersonal whoever the actor is: the event records a code going *to* somebody, and the name we have
  // is whoever caused it to be sent. "Dana emailed a code" would be the wrong way round.
  'guest.challenge_issued': () => 'A sign-in code was emailed',
  'guest.challenge_satisfied': ({ actorName }) => by(actorName, 'entered the right code', 'The right code was entered'),
  'guest.challenge_failed': ({ actorName }) => by(actorName, 'entered a wrong code', 'A wrong code was entered'),
  'guest.challenge_exhausted': ({ actorName }) =>
    by(actorName, 'entered too many wrong codes', 'Too many wrong codes were entered'),
  // "Signed in" on its own where the name is missing. English has no passive for it, and the bare fact is
  // truer than a construction invented to hold a subject that is not there.
  'guest.session_started': ({ actorName }) => by(actorName, 'signed in', 'Signed in'),
  'guest.device_trusted': ({ actorName }) =>
    by(actorName, 'chose to stay signed in on this device', 'A device was remembered'),
  // "viewed" rather than "opened", deliberately: `exchange.created` above already spends "opened this
  // exchange" on the event that made it, and two events reading identically on one screen is the
  // confusion this table exists to remove. No subject either, like the `exchange.*` entries above: the
  // subject is the exchange whose trail is being read, so naming it says nothing the reader lacks.
  'guest.exchange_viewed': ({ actorName }) => by(actorName, 'viewed this exchange', 'This exchange was viewed'),

  /*
    No sentence for embed.token_issued, and its absence is the decision rather than an oversight.

    An open widget mints a token every fourteen minutes for as long as the tab is up, so `core/activity`
    excludes the action from the projection at the read and it can no longer reach this panel at all. The
    words it used to carry, "opened this exchange from the CRM", claimed a person acted on every refresh
    but the first. The chain and the records export still keep every one of them.

    `backend/test/action-words.spec.ts` records the same reason in its EXCLUDED map and asserts that no
    action is ever both worded and excluded, so writing a sentence back in here fails that test rather
    than quietly restoring the line the day somebody relaxes the filter.
  */
  'access.records_read': ({ actorName }) =>
    by(actorName, 'read this exchange under records access', 'This exchange was read under records access'),
  'access.records_exported': ({ actorName }) =>
    by(actorName, 'exported this exchange under records access', 'This exchange was exported under records access'),
};

/** One line of the trail, or the raw action where there are no words for it. See `ACTION_WORDS`. */
function sentenceFor(event: ResolvedActivityEvent, custodianName: string | null): string {
  const words = ACTION_WORDS[event.action];
  if (words === undefined) return event.action;

  return words({ actorName: event.actorName, subjectName: event.subjectName, custodianName });
}

export interface ActivityPanelProps {
  readonly events: readonly ResolvedActivityEvent[];
  /**
   * The exchange's custodian, from the response envelope rather than from an event.
   *
   * Nullable and read by exactly one sentence; see `guest.grant_issued` in `ACTION_WORDS`. Required
   * rather than optional, so a surface that has the name cannot forget to pass it and quietly render the
   * anonymous fallback on the one line where naming somebody is the point.
   */
  readonly custodianName: string | null;
  /**
   * Whether the trail stops short of the beginning.
   *
   * `/activity` answers a page rather than the whole chain (DG.23), so a long-running exchange arrives
   * with its oldest events missing. Saying so is not a nicety: D-14 makes this trail the primary
   * compliance control, and one that quietly stops short is worse than one that says where it ends. The
   * caller decides, because the caller is what knows the page size it asked for.
   */
  readonly truncated?: boolean | undefined;
}

/**
 * The trail, capped and scrolled.
 *
 * It is the one list on this view with no upper bound. R3 appends an event for every state change and
 * R4 never removes one, so an exchange that has been open a year has hundreds and an uncapped panel
 * would push everything beside it off the screen - which is how it behaved until DG.18. The cap lives
 * in `surface.ts` so it can be a token-relative height rather than a number chosen here.
 *
 * `tabIndex` on the scroller is not decoration: a region that scrolls has to be reachable by keyboard,
 * or somebody who cannot use a pointer can read only the first few events. It gets a label with it,
 * because a focusable region with no name is announced as nothing at all.
 */
export function ActivityPanel({ events, custodianName, truncated }: ActivityPanelProps): React.JSX.Element {
  return (
    <Card title="Activity">
      {truncated === true && (
        /*
          Above the list, not below it, because the list is a scroller: a note under it is at the far end
          of the thing it is a note about, and this one is about what is missing from the top.
        */
        <p className="ddds-row__meta">
          The most recent {events.length} entries. Older ones are in a records export.
        </p>
      )}
      {events.length === 0 ? (
        <p className="ddds-empty">Nothing yet.</p>
      ) : (
        <ol className="ddds-list ddds-activity" tabIndex={0} role="region" aria-label="Activity, scrollable">
          {events.map((event) => (
            <Row
              key={`${event.seq}-${event.actorId}`}
              name={sentenceFor(event, custodianName)}
              /*
                The resolved name where there is one, and the kind where there is not.

                The kind is a bare word - "guest", "service" - which is what this line said for every
                event until DG.23. It stays as the fallback because it is still true, and because a
                sentence with no actor in it needs the second line to say what sort of thing acted.
              */
              meta={`${new Date(event.occurredAt).toLocaleString()} - ${event.actorName ?? event.actorKind}`}
            />
          ))}
        </ol>
      )}
    </Card>
  );
}

export interface FailureNoticeProps {
  readonly detail: string;
  readonly reference?: string | undefined;
  readonly children?: ReactNode;
}

/** The correlation id on screen, not in a console: it is the only thing a customer can read back to us. */
export function FailureNotice({ detail, reference, children }: FailureNoticeProps): React.JSX.Element {
  // Spread rather than passed, because `exactOptionalPropertyTypes` makes an absent prop and one set to
  // undefined different types, and `Notice` declares `reference?: string`.
  return (
    <Notice tone="error" {...(reference === undefined ? {} : { reference })}>
      {detail}
      {children}
    </Notice>
  );
}
